import type { Point } from '../types';

interface PointerLike {
  clientX: number;
  clientY: number;
}

export class MobileControls {
  private readonly root: HTMLElement;
  private readonly active: boolean;
  private readonly cleanup: Array<() => void> = [];

  private readonly container: HTMLDivElement | null;
  private readonly stick: HTMLDivElement | null;
  private readonly knob: HTMLDivElement | null;
  private readonly interactButton: HTMLButtonElement | null;

  private readonly stickAxis: Point = { x: 0, y: 0 };
  private interactDown = false;
  private interactPressed = false;
  private stickPointerId: number | null = null;

  constructor(root: HTMLElement, forceActive = false) {
    this.root = root;
    this.active = forceActive || 'ontouchstart' in window || navigator.maxTouchPoints > 0;

    if (!this.active) {
      this.container = null;
      this.stick = null;
      this.knob = null;
      this.interactButton = null;
      return;
    }

    this.container = document.createElement('div');
    this.container.className = 'mobile-controls';
    this.root.classList.add('mobile-mode');

    this.stick = document.createElement('div');
    this.stick.className = 'mobile-stick';
    this.knob = document.createElement('div');
    this.knob.className = 'mobile-stick__knob';
    this.stick.appendChild(this.knob);

    this.interactButton = document.createElement('button');
    this.interactButton.className = 'mobile-action mobile-action--interact';
    this.interactButton.type = 'button';
    this.interactButton.textContent = 'INTERACT';

    this.container.append(this.stick, this.interactButton);
    this.root.appendChild(this.container);
    this.registerEvents();
  }

  public isActive(): boolean {
    return this.active;
  }

  public getMovementAxis(): Point {
    return { ...this.stickAxis };
  }

  public isInteractDown(): boolean {
    return this.interactDown;
  }

  public consumeInteractPressed(): boolean {
    if (!this.interactPressed) {
      return false;
    }
    this.interactPressed = false;
    return true;
  }

  public dispose(): void {
    for (const callback of this.cleanup) {
      callback();
    }
    this.cleanup.length = 0;
    this.container?.remove();
    this.root.classList.remove('mobile-mode');
  }

  private registerEvents(): void {
    if (!this.stick || !this.knob || !this.interactButton) {
      return;
    }

    const stick = this.stick;
    const knob = this.knob;
    const interactButton = this.interactButton;
    const radius = 46;

    const updateStick = (pointer: PointerLike): void => {
      const rect = stick.getBoundingClientRect();
      const cx = rect.left + rect.width * 0.5;
      const cy = rect.top + rect.height * 0.5;
      const dx = pointer.clientX - cx;
      const dy = pointer.clientY - cy;
      const distance = Math.hypot(dx, dy);
      const clamped = Math.min(radius, distance);
      const angle = Math.atan2(dy, dx);
      const knobX = Math.cos(angle) * clamped;
      const knobY = Math.sin(angle) * clamped;

      this.stickAxis.x = knobX / radius;
      this.stickAxis.y = knobY / radius;
      knob.style.transform = `translate(${knobX}px, ${knobY}px)`;
    };

    const resetStick = (): void => {
      this.stickAxis.x = 0;
      this.stickAxis.y = 0;
      this.stickPointerId = null;
      knob.style.transform = 'translate(0px, 0px)';
    };

    const onStickPointerDown = (event: PointerEvent): void => {
      if (this.stickPointerId !== null) {
        return;
      }
      this.stickPointerId = event.pointerId;
      stick.setPointerCapture(event.pointerId);
      updateStick(event);
      event.preventDefault();
    };

    const onStickPointerMove = (event: PointerEvent): void => {
      if (event.pointerId !== this.stickPointerId) {
        return;
      }
      updateStick(event);
      event.preventDefault();
    };

    const onStickPointerUp = (event: PointerEvent): void => {
      if (event.pointerId !== this.stickPointerId) {
        return;
      }
      resetStick();
      event.preventDefault();
    };

    const onInteractDown = (event: PointerEvent): void => {
      this.interactDown = true;
      this.interactPressed = true;
      interactButton.classList.add('mobile-action--down');
      event.preventDefault();
    };

    const onInteractUp = (event: PointerEvent): void => {
      this.interactDown = false;
      interactButton.classList.remove('mobile-action--down');
      event.preventDefault();
    };

    stick.addEventListener('pointerdown', onStickPointerDown, { passive: false });
    stick.addEventListener('pointermove', onStickPointerMove, { passive: false });
    stick.addEventListener('pointerup', onStickPointerUp, { passive: false });
    stick.addEventListener('pointercancel', onStickPointerUp, { passive: false });

    interactButton.addEventListener('pointerdown', onInteractDown, { passive: false });
    interactButton.addEventListener('pointerup', onInteractUp, { passive: false });
    interactButton.addEventListener('pointercancel', onInteractUp, { passive: false });
    interactButton.addEventListener('pointerleave', onInteractUp, { passive: false });

    this.cleanup.push(() => stick.removeEventListener('pointerdown', onStickPointerDown));
    this.cleanup.push(() => stick.removeEventListener('pointermove', onStickPointerMove));
    this.cleanup.push(() => stick.removeEventListener('pointerup', onStickPointerUp));
    this.cleanup.push(() => stick.removeEventListener('pointercancel', onStickPointerUp));
    this.cleanup.push(() => interactButton.removeEventListener('pointerdown', onInteractDown));
    this.cleanup.push(() => interactButton.removeEventListener('pointerup', onInteractUp));
    this.cleanup.push(() => interactButton.removeEventListener('pointercancel', onInteractUp));
    this.cleanup.push(() => interactButton.removeEventListener('pointerleave', onInteractUp));
  }
}
